/**
 * QL-375 group 6 — the mutations behind the template picker (screen 09) and
 * Apply mandate (screen 08), with the invalidations each write implies.
 *
 *   - choosing a template changes the goal's mandate: the mandate check on
 *     both pages and the completion gate re-read
 *   - applying it fills a portfolio: its detail, the goal's portfolio list,
 *     every snapshot under it and the mandate check re-read
 */

import type { ApplyMandatePreviewRequest, MandateDecisionValue } from "@repo/shared/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  applyMandate,
  clearMandateDecision,
  clearMandateTemplate,
  clearPortfolioMandateDecision,
  previewApplyMandate,
  saveMandateDecision,
  savePortfolioMandateDecision,
  selectMandateTemplate,
  setMandateBasisPortfolio,
  updateMandateFromGoal,
} from "@/lib/api/flow-instances/mandate";
import { flowCompletionQueryKey } from "./use-flow-completion";
import { goalSnapshotQueryKeys } from "./use-goal-snapshots";
import { useInvalidatePortfolioSnapshots } from "./use-portfolio-snapshots";
import { invalidateGoalPortfolios, invalidatePortfolioFamily } from "./use-portfolios";
import { productAssessmentKeys } from "./use-product-assessment";

export function useSelectMandateTemplate(instanceId: string, goalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => selectMandateTemplate(instanceId, goalId, templateId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: goalSnapshotQueryKeys.mandate(goalId) });
      qc.invalidateQueries({ queryKey: flowCompletionQueryKey(instanceId) });
      // The portfolio pages read the same goal mandate.
      invalidateGoalPortfolios(qc, goalId);
    },
  });
}

/** QL-677 — the Mandate selection section's Clear. */
export function useClearMandateTemplate(instanceId: string, goalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => clearMandateTemplate(instanceId, goalId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: goalSnapshotQueryKeys.mandate(goalId) });
      qc.invalidateQueries({ queryKey: flowCompletionQueryKey(instanceId) });
      invalidateGoalPortfolios(qc, goalId);
    },
  });
}

// QL-677 — the request shape is the shared contract: `templateId` builds from
// a picked template; absent = the goal's mandate.
export function usePreviewApplyMandate(instanceId: string, portfolioId: string) {
  return useMutation({
    mutationFn: (request: ApplyMandatePreviewRequest) =>
      previewApplyMandate(instanceId, portfolioId, request),
  });
}

export function useApplyMandate(instanceId: string, goalId: string, portfolioId: string) {
  const qc = useQueryClient();
  const invalidatePortfolioSnapshots = useInvalidatePortfolioSnapshots();
  return useMutation({
    mutationFn: (request: ApplyMandatePreviewRequest) =>
      applyMandate(instanceId, portfolioId, request),
    onSuccess: (data) => {
      invalidatePortfolioFamily(qc, portfolioId);
      invalidateGoalPortfolios(qc, goalId);
      // The server names which of the portfolio's snapshots it regenerated;
      // the goal-level charts merge them, so the goal's snapshots re-read too.
      if (data.refreshHints) {
        invalidatePortfolioSnapshots(data.refreshHints.portfolioId, data.refreshHints);
      }
      qc.invalidateQueries({ queryKey: goalSnapshotQueryKeys.allForGoal(goalId) });
      qc.invalidateQueries({ queryKey: flowCompletionQueryKey(instanceId) });
      // Apply rewrote the holdings: the product-assessment in-use set follows,
      // from both entry points (the builder and the deviation card's footer).
      qc.invalidateQueries({ queryKey: productAssessmentKeys.productTypesInUse(instanceId) });
      // QL-695 / QL-730 B7 — on an account follow-up Apply writes sell
      // decisions on the frozen holdings (full sells, sell-downs): the
      // builder's existing-snapshot read must follow, or its Keep / Sell
      // column shows the old decision until a reload.
      qc.invalidateQueries({ queryKey: ["existing-snapshot", portfolioId] });
    },
  });
}

/**
 * The deviation decision — the goal's (goal-level blocking), or one
 * portfolio's own when `portfolioId` is given (group 7, portfolio-level
 * blocking). Null clears it. Both scopes share the goal's mandate key prefix.
 */
export function useMandateDecision(
  instanceId: string,
  goalId: string,
  portfolioId: string | null = null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { decision: MandateDecisionValue | null; justification?: string }) =>
      portfolioId
        ? args.decision === null
          ? clearPortfolioMandateDecision(instanceId, portfolioId)
          : savePortfolioMandateDecision(instanceId, portfolioId, args)
        : args.decision === null
          ? clearMandateDecision(instanceId, goalId)
          : saveMandateDecision(instanceId, goalId, args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: goalSnapshotQueryKeys.mandate(goalId) });
      qc.invalidateQueries({ queryKey: flowCompletionQueryKey(instanceId) });
    },
  });
}

/** Group 7 — screen 07's Update: the goal's mandate changes, so every reader re-reads. */
/** QL-677 — "Create mandate based on this portfolio": the portfolio becomes the mandate's basis. */
export function useSetMandateBasisPortfolio(
  instanceId: string,
  goalId: string,
  portfolioId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => setMandateBasisPortfolio(instanceId, portfolioId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: goalSnapshotQueryKeys.mandate(goalId) });
      qc.invalidateQueries({ queryKey: flowCompletionQueryKey(instanceId) });
      invalidateGoalPortfolios(qc, goalId);
    },
  });
}

/** QL-664 — the goal page's Update: the goal's mandate changes, so every reader re-reads. */
export function useUpdateMandateFromGoal(instanceId: string, goalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => updateMandateFromGoal(instanceId, goalId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: goalSnapshotQueryKeys.mandate(goalId) });
      qc.invalidateQueries({ queryKey: flowCompletionQueryKey(instanceId) });
      invalidateGoalPortfolios(qc, goalId);
    },
  });
}
